import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AUDIENCE_INSIGHTS_DROPDOWN } from '@core/enum/dashboard';
import { WeekDataDTO } from '@core/models/interfaces/marketing-dashboard/dashboard';
import { TranslateModule } from '@ngx-translate/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule
} from 'ng-apexcharts';
import { TooltipModule } from 'primeng/tooltip';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  title: ApexTitleSubtitle;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: 'app-heatmap',
  standalone: true,
  imports: [TooltipModule, NgApexchartsModule, TranslateModule, FormsModule, CommonModule],
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss'],
  providers: [DecimalPipe]
})
export class HeatMapComponent implements OnChanges {
  @Input() data: WeekDataDTO[];
  @Input() selectedDataTypeDayAndTime: AUDIENCE_INSIGHTS_DROPDOWN = AUDIENCE_INSIGHTS_DROPDOWN.AMOUNT_SPENT;
  readonly translatePrefix = 'section-shop-dashboard.';
  chartOptions: ChartOptions = {
    series: [],
    chart: {
      type: 'heatmap'
    },
    dataLabels: {},
    title: {},
    xaxis: {},
    yaxis: {},
    plotOptions: {}
  };

  // HOURS_CONFIG = [
  //   ...Array.from({ length: 24 }, (_, i) => i),
  //   // ...Array.from({ length: 11 }, (_, i) => i + 1),
  // ];
  HOURS_CONFIG = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '00:00'];

  COLOR_CONFIG = ['#DBEAFE', '#BFDBFE', '#93C5FD', '#3B82F6', '#1D4ED8'];

  constructor(private decimalPipe: DecimalPipe) {}

  ngOnChanges(changes: SimpleChanges) {
    this.data = [...this.data].reverse();
    const dataConverted = this.convertData(this.data);
    const ranges = this.getRanges(dataConverted);
    const fiveParts = this.divideRangeIntoFiveParts(ranges.min, ranges.max);

    if (changes?.['selectedDataTypeDayAndTime']?.currentValue) {
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          enabled: false
        },
        chart: {
          height: 320,
          type: 'heatmap',
          toolbar: {
            show: false
          },
          parentHeightOffset: 0
        },
        title: {
          text: undefined
        },
        xaxis: {
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: true
          }
        },
        yaxis: {
          labels: {
            formatter: (val: number) => {
              if (typeof val !== 'number') {
                return val;
              }

              const value = this.decimalPipe.transform(val, '1.0-2') || '0';
              if (this.isDisplayAsInteger(this.selectedDataTypeDayAndTime)) {
                return `${value}`;
              } else if (this.selectedDataTypeDayAndTime === AUDIENCE_INSIGHTS_DROPDOWN.CTR) {
                return `${value}%`;
              }
              return `$${value}`;
            },
            style: {
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: '"Inter", "PlusJakartaSans", "Mulish", sans-serif'
            },
            offsetX: 0,
            offsetY: 0
          }
        }
      };
    }

    if (this.data.length === 1) {
      this.chartOptions.chart.height = 84;
    } else {
      this.chartOptions.chart.height = 320;
    }

    this.chartOptions.series = this.data.map(item => ({
      name: item.day,
      data: item.value?.slice(0, 24) ?? []
    }));

    this.chartOptions.plotOptions = {
      heatmap: {
        radius: 0,
        enableShades: false,
        distributed: true,
        colorScale: {
          ranges: [...fiveParts]
        }
      }
    };
  }

  getRanges(data: Array<number[]>) {
    let min = data?.[0]?.[0];
    let max = data?.[0]?.[0];
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[i].length; j++) {
        if (max < data[i][j]) {
          max = data[i][j];
        }
        if (min > data[i][j]) {
          min = data[i][j];
        }
      }
    }
    return { min, max };
  }

  divideRangeIntoFiveParts(min: number, max: number) {
    if (max === 0) {
      return [{ from: 0, to: 0, color: this.COLOR_CONFIG[0] }];
    }

    let partSize = (max - min) / 5;
    let parts = [];
    parts.push({
      from: 0,
      to: 0,
      color: this.COLOR_CONFIG[0]
    });

    parts.push({
      from: 0.00000001,
      to: partSize - 0.0001,
      color: this.COLOR_CONFIG[1]
    });

    for (let i = 1; i < 4; i++) {
      let partMin = min + partSize * i;
      let partMax = partMin + partSize - (i < 3 ? 0.0001 : 0);
      parts.push({
        from: partMin,
        to: i < 3 ? partMax : max,
        i: i,
        color: this.COLOR_CONFIG[i + 1]
      });
    }

    return parts;
  }

  convertData(data: WeekDataDTO[]): Array<number[]> {
    const results: Array<number[]> = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].value) {
        results.push(data[i].value?.slice(0, 24) as number[]);
      } else {
        results.push([]);
      }
    }
    return results;
  }

  isDisplayAsInteger(code: AUDIENCE_INSIGHTS_DROPDOWN): boolean {
    const integerFields = [
      AUDIENCE_INSIGHTS_DROPDOWN.MESSAGE_CONVERSATIONS_STARTED,
      AUDIENCE_INSIGHTS_DROPDOWN.CONVERSIONS,
      AUDIENCE_INSIGHTS_DROPDOWN.IMPRESSIONS,
      AUDIENCE_INSIGHTS_DROPDOWN.CLICKS_ALL
    ];
    return integerFields.includes(code);
  }
}
